import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Attribution } from 'src/interfaces';
import { fetchAttributionByChain } from 'src/utils/fetchAttributionByChain';
import { TBlockchain } from 'src/types';

export function useAttributionForAddress(chain: TBlockchain, address?: string) {
  const queryClient = useQueryClient();

  // Only normalize to lowercase for EVM chains, Bitcoin addresses are case-sensitive
  const normalizedAddress =
    chain === 'bitcoin' ? address : address?.toLowerCase();

  const { data: attribution, isLoading } = useQuery<Attribution, Error>({
    // 1. Pass everything as one object
    queryKey: ['attribution', chain, normalizedAddress],
    queryFn: async () => {
      if (!normalizedAddress) throw new Error('Address is required');
      const map = await fetchAttributionByChain(chain, [normalizedAddress]);
      const result = map[normalizedAddress];
      if (!result) throw new Error(`No attribution for ${normalizedAddress}`);
      return result;
    },
    // 2. Only run if we have an address
    enabled:
      Boolean(normalizedAddress) &&
      queryClient.getQueryData(['attribution', chain, normalizedAddress]) !==
        undefined,
    // 3. Seed from the cache entry your batch loader created
    initialData: () =>
      queryClient.getQueryData<Attribution>([
        'attribution',
        chain,
        normalizedAddress,
      ]),
    // 4. Never consider it stale
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30, // 30 minutes
  });

  return { attribution, isLoading };
}
